import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, User, Building2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecipientFormPage() {
  const { token } = useParams();
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', document_number: '', bank_name: '',
    account_type: 'pix', pix_key: '', account_number: '', branch_number: '',
    nationality: '', postal_code: '', address: '', phone: '', email: '',
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/public/recipient-invite/${token}`)
      .then(res => {
        setInvite(res.data);
        setForm(prev => ({
          ...prev,
          full_name: res.data.recipient_name || '',
          email: res.data.recipient_email || '',
        }));
      })
      .catch(err => {
        if (err.response?.status === 400) setError('expired');
        else setError('not_found');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.document_number) {
      toast.error('名前とCPF番号は必須です');
      return;
    }
    if (form.account_type === 'pix' && !form.pix_key) {
      toast.error('PIXキーは必須です');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/recipient-invite/${token}/submit`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_occurred'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header t={t} />
        <main className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-primary mb-2">
            {error === 'expired' ? t('invite_form_expired') : t('invite_form_not_found')}
          </h1>
        </main>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header t={t} />
        <main className="max-w-lg mx-auto px-4 py-16 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-primary mb-2" data-testid="invite-success-title">{t('invite_form_success_title')}</h1>
          <p className="text-muted-foreground">{t('invite_form_success_desc')}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header t={t} />
      <main className="max-w-2xl mx-auto px-4 py-8 md:py-12" data-testid="recipient-form-page">
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-2">{t('invite_form_title')}</h1>
          <p className="text-muted-foreground">
            {t('invite_form_subtitle', { sender: invite?.sender_name || '' })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-border/50 rounded-2xl p-6 space-y-6">
          {/* Personal Info */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />{t('recipients_personal_info')}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('recipients_name')} *</Label>
                  <Input value={form.full_name} onChange={e => update('full_name', e.target.value)} required data-testid="invite-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('recipients_cpf')} *</Label>
                  <Input value={form.document_number} onChange={e => update('document_number', e.target.value)} placeholder="123.456.789-00" required data-testid="invite-cpf-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('recipients_nationality')}</Label>
                  <Input value={form.nationality} onChange={e => update('nationality', e.target.value)} placeholder="Brasil" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('recipients_postal_code')}</Label>
                  <Input value={form.postal_code} onChange={e => update('postal_code', e.target.value)} placeholder="01000-000" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t('recipients_address')}</Label>
                <Input value={form.address} onChange={e => update('address', e.target.value)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('recipients_phone')}</Label>
                  <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+55 11 99999-9999" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('recipients_email')}</Label>
                  <Input value={form.email} onChange={e => update('email', e.target.value)} type="email" />
                </div>
              </div>
            </div>
          </div>

          {/* Bank Info */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4" />{t('recipients_bank_info')}
            </h3>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t('recipients_method')} *</Label>
                <Select value={form.account_type} onValueChange={v => update('account_type', v)}>
                  <SelectTrigger data-testid="invite-account-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="bank_account">{t('recipients_bank_account')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.account_type === 'pix' ? (
                <div className="space-y-1.5">
                  <Label>{t('recipients_pix')} *</Label>
                  <Input value={form.pix_key} onChange={e => update('pix_key', e.target.value)} placeholder={t('recipients_pix_placeholder')} required data-testid="invite-pix-input" />
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label>{t('recipients_bank')} *</Label>
                    <Input value={form.bank_name} onChange={e => update('bank_name', e.target.value)} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>{t('recipients_branch_number')} *</Label>
                      <Input value={form.branch_number} onChange={e => update('branch_number', e.target.value)} required />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('recipients_account')} *</Label>
                      <Input value={form.account_number} onChange={e => update('account_number', e.target.value)} required />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="w-full rounded-full" data-testid="invite-submit-btn">
            {submitting ? t('invite_form_submitting') : t('invite_form_submit')}
          </Button>
        </form>
      </main>
      <footer className="border-t border-border bg-secondary/30 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; 2026 PRC Remit. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function Header({ t }) {
  return (
    <header className="border-b border-border/50 bg-white/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logoUrl} alt="PRC Remit" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-logo text-2xl font-bold text-primary">PRC Remit</span>
          </div>
          <LanguageSelector />
        </div>
      </div>
    </header>
  );
}
